/**
 * Mobile Feed Constants
 *
 * This module contains constants for mobile feed construction including:
 * - Card sizes and components for feed rendering
 * - Item types and article elements
 * - Advertisement configuration
 * - SSR/BaaS platform identifiers
 *
 * @module baas/common/constants/MobileFeedConstants
 */

/**
 * Card sizes for mobile feeds (phone only)
 */
export const CARD_SIZES = {
  // Large featured image with headline and standfirst stacked vertically
  FEATURED: "Featured",

  // Headline with right-aligned thumbnail image laid out horizontally
  STANDARD: "Standard",

  // Category/section header cards
  CATEGORY: "SectionTitle",

  // Video section cards
  PORTRAIT_VIDEO: "PortraitVideo",

  // Video section cards
  STORIES_CAROUSEL: "StoriesCarousel",

  // Video section cards
  CAROUSEL: "Carousel",

  // Advertisement card
  ADVERTISEMENT: "Advertisement",

  // Discovery Widget cards
  WIDGETSLIST: "WidgetsList",

  // WebBanner cards
  WEBBANNER: "WebBanner",

  // Word Quiz card
  WORD_QUIZ: "WordQuiz",

  // TM Picks grouped articles card (multiple articles as items in one swipeable card)
  STACKED_SWIPABLE: "StackedSwipable",

  // TM Picks individual article cards (used in section feed / pagination)
  TM_PICK: "TmPick",

  // Top News grouped articles card (multiple articles as items in one horizontal thumbnail card)
  HORIZONTAL_THUMBNAIL: "HorizontalThumbnail",

  // Most Read grouped articles card (multiple articles as items in one stacked list card)
  STACKED_LIST: "StackedList",
} as const;

/**
 * Card components that tell mobile app which UI elements to render
 */
export const CARD_COMPONENTS = {
  HEADLINE: "Headline",
  STANDFIRST: "Standfirst",
  TIMESTAMP: "Timestamp",
  IMAGE: "Image",
  AUTHORS: "Authors",
  RUNTIME: "Runtime",
  SHARE: "Share",
  PLAY: "Play",
  BOOKMARK: "Bookmark",
} as const;

/**
 * Item types for feed items
 */
export const ITEM_TYPES = {
  ARTICLE: "Article",
  CATEGORY: "Category",
  PODCAST: "PodcastEpisode",
  VIDEO: "Video",
  WEBBANNER: "WebBanner",
  BANNER: "Banner", // Ad banner
  WORD_QUIZ: "WordQuiz",
} as const;

/**
 * Default page and container article counts for mobile feeds
 */
export const MOBILE_CONSTANTS = {
  DEFAULT_PAGE_SIZE: 20,
  MAX_PAGE_SIZE: 40,
  DEFAULT_CONTAINER_ARTICLE_COUNT: 3,
  SPECIAL_CONTAINER_ITEM_COUNT: 5,
  PODCAST_PAGE_SIZE: 10,
} as const;

/**
 * Mobile context variant for SSR (used to identify mobile app context)
 */
export const MOBILE_CONTEXT_VARIANT: string = "app";

/** Platform identifier used by SSR generators for logging */
export const SSR_PLATFORM_IDENTIFIER = "mobile-server-side-request" as const;

/**
 * Mobile API path prefixes
 * Used for route registration and pagination path metadata
 */
export const MOBILE_API_PATHS = {
  HEADER_MENU: "/v1/header-menu",
  WORD_QUIZ: "/v1/word-quiz",
  TRENDING: "/v1/trending",
  WIDGETS: "/v1/widgets",
  SECTION_FEED: "/v1/feed/section/",
  SEARCH_FEED: "/v1/feed/search",
  KEYWORD_FEED: "/v1/feed/keyword/",
  MEDIA_PODCASTS: "/v1/feed/media/podcasts",
  MEDIA_VIDEOS: "/v1/feed/media/videos",
  PORTRAIT_VIDEOS: "/v1/feed/media/portrait-videos",
  LANDSCAPE_VIDEOS: "/v1/feed/media/landscape-videos",
} as const;

/**
 * Export the derived type.
 * This is CRITICAL for cross-file type safety.
 */
export type MobileApiPath =
  (typeof MOBILE_API_PATHS)[keyof typeof MOBILE_API_PATHS];

/**
 * Platform-specific identifiers for ad configuration
 * These match the keys in adsConfig JSON files
 */
export const PLATFORM_KEYS = {
  ANDROID: "tmapp_android",
  IOS: "tmapp_ios",
} as const;

/**
 * Advertisement page types enum
 * Defines the different page types that can have advertisements
 */
export enum ADVERTISEMENT_PAGE_TYPE {
  HOME = "home",
  SECTION = "section",
  ARTICLE = "article",
  LISTING = "listing",
  TAGS_LISTING = "tags",
  AUTHOR = "authors",
}

/**
 * Ad position field enum
 * Defines which position field to use for ad injection
 * - POSITION: Used for free users (standard ad placements)
 * - POSITION_SUBSCRIBER: Used for premium users (fewer ads or different placements)
 */
export enum AD_POSITION_FIELD {
  POSITION = "position",
  POSITION_SUBSCRIBER = "positionSubscriber",
}

/**
 * Article element types
 * Defines all possible element types in an article's storyline
 */
export const ARTICLE_ELEMENTS = {
  PARAGRAPH: "paragraph",
  IMAGE: "image",
  DYNAMIC_IFRAME_WRAPPER: "dynamic_iframe_wrapper",
  EMBED: "embed",
  GROUPED_RELATION: "grouped_relation",
  SUB_HEAD: "sub_head",
  LIST_BULLETED: "list_bulleted",
  LIST_NUMBERED: "list_numbered",
  HORIZONTAL_ROW: "horizontal_row",
  GRAPHIC: "graphic",
  BRIGHTCOVE_VIDEO: "brightcove_video",
  YOUTUBE_VIDEO: "youtube_video",
  OMNY_PODCAST: "omny_podcast",
  NESTED_ELEMENTS: "nested_elements",
  ADVERTISEMENT: "advertisement",
  KICKER: "kicker",
  STANDFIRST: "standfirst",
  ARTICLE_HEAD: "article_head",
  SCROLLY: "scrolly",
  CREDITS: "credits",
  QUOTE: "pull_quote",
  PHOTO_GRID: "photo_grid",
} as const;

/**
 * Error Messages
 * User-facing error messages for mobile API and SSR responses
 */
export const ERROR_MESSAGES = {
  INVALID_PLATFORM_HEADER: "Missing or Invalid header parameter(s)",
  INVALID_SECTION: "Invalid section parameter",
  HEADER_MENU_FAILED: "Unable to load menu. Please try again later",
  WORD_QUIZ_FAILED: "Unable to load word quiz. Please try again later",
  VIDEOS_FAILED: "Unable to load videos. Please try again later",
  PODCAST_FAILED: "Unable to load podcasts. Please try again later",
  HOME_FEED_FAILED: "Unable to load home feed. Please try again later",
  SECTION_FEED_FAILED: "Unable to load section feed. Please try again later",
  PAGINATION_FAILED: "Unable to load more articles. Please try again later",
  MISSING_MAX_DATE: "Missing mandatory parameter: maxDate",
  DISCOVERY_WIDGET_FAILED:
    "Unable to load the discovery widget. Please try again later",
  SEARCH_FEED_FAILED: "Unable to load search feed. Please try again later",
  INVALID_KEYWORD: "Missing or invalid keyword parameter",
  KEYWORD_FEED_FAILED: "Unable to load keyword feed. Please try again later",
  TRENDING_FAILED: "Unable to load trending data. Please try again later",
} as const;

/**
 * Video provider identifiers used across mobile video endpoints
 */
export const VIDEO_PROVIDERS = {
  PORTRAIT_BRIGHTCOVE: "video/portraitBrightcove",
  LANDSCAPE_BRIGHTCOVE: "video/Brightcove",
} as const;

/** Type exports */
export type CardSize = (typeof CARD_SIZES)[keyof typeof CARD_SIZES];
export type CardComponent =
  (typeof CARD_COMPONENTS)[keyof typeof CARD_COMPONENTS];
export type ItemType = (typeof ITEM_TYPES)[keyof typeof ITEM_TYPES];
